import {ChangeDetectionStrategy, Component, computed, inject, signal} from '@angular/core';
import {NotificationDialogContentComponent} from "../notification-dialog-content.component";
import {NotificationPage} from "../../multi-page-notification/multi-page-notification.component";
import {RatingModule} from "primeng/rating";
import {RatingService} from "../../../rating.service";
import {firstValueFrom} from "rxjs";
import {AnalyticsService} from "../../../analytics.service";
import {DynamicDialogRef} from "primeng/dynamicdialog";
import {SubmitButtonComponent} from "../../../../components/submit-button/submit-button.component";
import {RateHomeyExperienceCommand} from "homey-housing-lottery-app-dto";
import {Textarea} from "primeng/textarea";
import {FloatLabel} from "primeng/floatlabel";
import {FormsModule} from "@angular/forms";
import {FormButtonsComponent} from "../../../../components/form-buttons/form-buttons.component";

type RateNotificationData = { anonymousId?: string };

@Component({
  selector: 'homey-rate-notification-dialog-content',
  imports: [
    NotificationDialogContentComponent,
    RatingModule,
    SubmitButtonComponent,
    Textarea,
    FloatLabel,
    FormsModule,
    FormButtonsComponent,
  ],
  templateUrl: './rate-notification-dialog-content.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RateNotificationDialogContentComponent extends NotificationPage<RateNotificationData> {
  private readonly dialogRef = inject(DynamicDialogRef);
  private readonly ratingService = inject(RatingService);
  private readonly analyticsService = inject(AnalyticsService);
  protected readonly step = signal<'rating' | 'feedback' | 'done'>('rating');
  protected readonly rating = signal(0);
  protected readonly description = signal('');
  private readonly ratingEventSent = signal(false);
  protected readonly title = computed(() => {
    if (this.step() === 'done') {
      return 'Thanks for your feedback';
    }
    return 'Rate your Homey experience';
  });
  protected readonly primaryLabel = computed(() => {
    if (this.step() === 'done') {
      return 'Continue';
    }
    if (this.step() === 'feedback') {
      return 'Submit feedback';
    }
    return 'Submit rating';
  });
  protected readonly bodyMessage = computed(() => {
    if (this.step() === 'done') {
      return 'It helps us shape a smoother, more helpful Homey experience for all.';
    }
    if (this.step() === 'feedback') {
      return 'Mind telling us what changes we could make? Your feedback helps us improve Homey.';
    }
    return 'Your feedback helps us make Homey better for everyone trying to find an affordable place to live.';
  });

  protected readonly onFormAction = async (choice: 'yes' | 'no') => {
    if (choice === 'no') {
      return this.persistFeedback({ type: 'delayed' }, { closeOnSuccess: true });
    }

    if (this.step() === 'done') {
      this.dialogRef.close()
      return;
    }

    if (this.step() === 'rating') {
      this.sendRatingEventIfNeeded();
      this.step.set('feedback');
      return;
    }

    return this.persistFeedback({
      type: 'reviewed',
      rating: this.rating(),
      description: this.description(),
    }, { closeOnSuccess: false });
  }

  protected onConfirm = async () => {
    if (this.step() === 'done') {
      this.dialogRef.close();
      return;
    }

    if (this.step() === 'rating') {
      this.sendRatingEventIfNeeded();
      this.step.set('feedback');
      return;
    }

    await this.persistFeedback({
      type: 'reviewed',
      rating: this.rating(),
      description: this.description(),
    }, { closeOnSuccess: false });
  }

  private async persistFeedback(command: RateHomeyExperienceCommand, options: { closeOnSuccess?: boolean } = {}) {
    if (command.type === 'reviewed' && (!command.rating || command.rating < 1)) {
      return;
    }

    try {
      const anonymousId = this.data().anonymousId;
      const origin = anonymousId ? 'anonymous' : 'in-app';
      await firstValueFrom(this.ratingService.submitRating(
        anonymousId ? {...command, anonymousId} : command
      ));
      if (command.type === 'reviewed') {
        this.analyticsService.sendEvent('rate-homey-description-submitted', {
          rating: command.rating,
          origin,
        });
        this.step.set('done');
      }
      if (options.closeOnSuccess) {
        this.dialogRef.close();
      }
    } catch (error) {
      console.error("Failed to save user rating", error);
    }
  }

  private sendRatingEventIfNeeded() {
    if (this.ratingEventSent() || !this.rating()) {
      return;
    }

    const origin = this.data().anonymousId ? 'anonymous' : 'in-app';
    this.analyticsService.sendEvent('rate-homey-rating-submitted', {
      rating: this.rating(),
      origin,
    });
    this.ratingEventSent.set(true);
  }
}
